// Any implementation of replaceHydrateFunction
// Please check the docs here for additional requirements
// https://www.npmjs.com/package/gatsby-plugin-loadable-components-ssr

import posthog from 'posthog-js';
import { getPostHogEnvironmentProperties } from './src/utils/posthogEnvironment';

export const onClientEntry = () => {
    if (typeof window === 'undefined') return;
    // Guard against double-init (Gatsby fast-refresh can re-run this hook).
    if (window.posthog && window.posthog.__loaded) return;
    const apiKey = process.env.GATSBY_POSTHOG_API_KEY;
    if (!apiKey) return;
    const enabledEnvs = ['production', 'stage', 'development'];
    if (enabledEnvs.indexOf(process.env.NODE_ENV) === -1) return;
    const postHogEnvironmentProperties = getPostHogEnvironmentProperties(
        window.location.hostname,
        process.env.NODE_ENV
    );

    // The project's existing PostHog helpers (src/utils/posthog.ts et al.)
    // read the instance off `window.posthog`, which the snippet-style loader
    // used to set but ES-module import does not. Assign it explicitly.
    window.posthog = posthog;

    posthog.init(apiKey, {
        api_host: 'https://eu.i.posthog.com',
        // With 'identified_only', PostHog creates person profiles only after
        // calling posthog.identify(), not for anonymous users.
        person_profiles: 'identified_only',
        disable_session_recording: true,
        session_recording: {
            maskAllInputs: true,
            maskInputOptions: {
                email: true
            }
        },
        capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: true,
            capture_console_errors: false
        },
        // Drop unhandled exceptions thrown by scripts that in-app browsers
        // (Facebook, Instagram, TikTok, etc.) inject into our pages. These
        // are third-party @JavascriptInterface / webkit message-handler calls
        // that race the WebView teardown and throw "Java object is gone", or
        // reference undefined webkit.messageHandlers. We ship zero native
        // bridge code ourselves, so anything matching these patterns is IAB
        // noise.
        before_send: (event) => {
            if (!event) return event;
            event.properties = {
                ...event.properties,
                ...postHogEnvironmentProperties
            };
            if (event.event !== '$exception') return event;
            const props = event.properties || {};
            const values = [];
            if (Array.isArray(props.$exception_values)) {
                values.push(...props.$exception_values);
            }
            if (Array.isArray(props.$exception_list)) {
                for (const ex of props.$exception_list) {
                    if (ex && typeof ex.value === 'string') values.push(ex.value);
                }
            }
            const joined = values.join(' | ');
            if (!joined) return event;
            if (/Java object is gone/i.test(joined)) return null;
            if (joined.indexOf('enableDidUserTypeOnKeyboardLogging') !== -1) return null;
            if (/Error invoking .+:/i.test(joined) && /is gone/i.test(joined)) return null;
            if (/webkit\.messageHandlers.*postMessage/.test(joined)) {
                const ua = props.$raw_user_agent || '';
                if (/FB_IAB|FBAN|FBAV|Instagram|Line\/|MicroMessenger|TikTok/i.test(ua)) {
                    return null;
                }
            }
            return event;
        },
        loaded: (ph) => {
            if (process.env.NODE_ENV === 'development') {
                ph.debug();
            }
        }
    });
    posthog.register(postHogEnvironmentProperties);
    // Also expose the environment to feature-flag targeting: the PostHog project is
    // shared across environments and nothing identifies users, so a flag can only
    // be scoped by these person properties (e.g. app_environment = staging).
    posthog.setPersonPropertiesForFlags(postHogEnvironmentProperties);
};
